import {useCallback, useEffect, useRef, useState} from 'react';
import {Client, Frame, IMessage, StompConfig} from '@stomp/stompjs';
import SockJS from 'sockjs-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET!;
const HEARTBEAT_MS = 20_000;

type Status = 'connecting' | 'connected' | 'disconnected' | 'error';

export function useStomp() {
    const clientRef = useRef<Client | null>(null);

    const subscriptions = useRef<Array<() => () => void>>([]);
    const activeUnsubs = useRef<Array<() => void>>([]);

    const [status, setStatus] = useState<Status>('connecting');

    const retry = useRef(0);
    const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const backoffDelay = () => {
        const base = Math.min(30_000, 1000 * 2 ** retry.current);
        const jitter = 0.5 + Math.random();
        retry.current = Math.min(retry.current + 1, 15);
        return Math.floor(base * jitter);
    };

    const clearReconnectTimer = () => {
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
    };

    const scheduleReconnect = useCallback(() => {
        clearReconnectTimer();
        const delay = backoffDelay();
        reconnectTimerRef.current = setTimeout(() => {
            connect();
        }, delay);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isActivatingRef = useRef(false);
    const generationRef = useRef(0);

    const cleanupActive = () => {
        activeUnsubs.current.forEach((u) => {
            try { u(); } catch {}
        });
        activeUnsubs.current = [];
    };

    const hardShutdown = useCallback(async () => {
        clearReconnectTimer();
        const client = clientRef.current;
        clientRef.current = null;

        cleanupActive();

        if (client) {
            try {
                await client.deactivate();
            } catch {
            }
        }
    }, []);

    const connect = useCallback(async () => {
        if (isActivatingRef.current) return;
        isActivatingRef.current = true;

        setStatus('connecting');

        await hardShutdown();

        const myGen = generationRef.current + 1;
        generationRef.current = myGen;

        const config: StompConfig = {
            webSocketFactory: () => new SockJS(SOCKET_URL),
            heartbeatOutgoing: HEARTBEAT_MS,
            heartbeatIncoming: HEARTBEAT_MS,
            connectionTimeout: 5_000,
            reconnectDelay: 0,
            debug: (msg) => console.debug('[STOMP]', msg),

            onConnect: (_frame: Frame) => {
                if (generationRef.current !== myGen) return;

                setStatus('connected');
                retry.current = 0;

                // rewire all subscriptions for this connection
                subscriptions.current.forEach((makeSub) => {
                    const unsub = makeSub();
                    activeUnsubs.current.push(unsub);
                });
            },

            onStompError: () => {
                if (generationRef.current !== myGen) return;
                setStatus('error');
                scheduleReconnect();
            },
            onWebSocketClose: () => {
                if (generationRef.current !== myGen) return;
                setStatus('disconnected');
                scheduleReconnect();
            },
            onWebSocketError: () => {
                if (generationRef.current !== myGen) return;
                setStatus('error');
                scheduleReconnect();
            },
        };

        const client = new Client(config);
        clientRef.current = client;
        try {
            client.activate();
        } finally {
            isActivatingRef.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hardShutdown, scheduleReconnect]);

    useEffect(() => {
        connect();

        const handleVisibility = () => {
            if (document.visibilityState === 'visible' && !clientRef.current?.connected) {
                scheduleReconnect();
            }
        };
        const handleOnline = () => {
            if (!clientRef.current?.connected) {
                scheduleReconnect();
            }
        };

        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('online', handleOnline);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('online', handleOnline);

            (async () => {
                await hardShutdown();
                setStatus('disconnected');
            })();
        };
    }, [connect, hardShutdown, scheduleReconnect]);

    const subscribe = useCallback(
        <T,>(topic: string, handler: (body: T) => void) => {
            const makeSub = () => {
                const sub = clientRef.current!.subscribe(topic, (msg: IMessage) => {
                    try {
                        handler(JSON.parse(msg.body) as T);
                    } catch (e) {
                        console.error('WS JSON parse error:', e, msg.body);
                        handler(msg.body as unknown as T);
                    }
                });
                return () => {
                    try { sub.unsubscribe(); } catch {}
                };
            };

            subscriptions.current.push(makeSub);

            let unsub = () => {};
            if (clientRef.current?.connected) {
                unsub = makeSub();
                activeUnsubs.current.push(unsub);
            }

            return () => {
                try { unsub(); } catch {}
                activeUnsubs.current = activeUnsubs.current.filter((u) => u !== unsub);
                subscriptions.current = subscriptions.current.filter((f) => f !== makeSub);
            };
        },
        []
    );

    const sendMessage = useCallback((destination: string, body: any) => {
        const client = clientRef.current;
        if (client?.connected) {
            try {
                client.publish({
                    destination,
                    body: JSON.stringify(body),
                    headers: { 'content-type': 'application/json' },
                });
            } catch (e) {
                console.error('STOMP publish failed', e);
            }
        } else {
            console.warn('STOMP not connected – message dropped', destination, body);
        }
    }, []);

    return { status, subscribe, sendMessage };
}
